import React, { useState, useCallback, useRef } from "react";
import {
  ThemeProvider,
  createTheme,
  Button,
  Chip,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import {
  Wrapper,
  InnerWrapper,
  InnerFooterBtnBox,
  InnerSection,
  Title,
  SectionBox,
  InnerSectionBox,
  InnerSectionContentContainer,
} from "./styles/styled.components";
import SchedulingSelector from "./components/SchedulingSelector";
import OptionsArea from "./components/OptionsArea";
import ModelInput from "./components/ModelInput";
import { CHECKBOX_OPTIONS } from "./constants/config";
import {
  useGetTableData2,
  useCreateModelData,
  usePutData,
} from "../Plan/tableHooks";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import RuleList from "./components/RuleList";
import { useParams } from "react-router-dom";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import goToHelpLink from "../../../utils/goToHelpLink";

const theme = createTheme({
  typography: {
    fontFamily: "Pretendard, system-ui, -apple-system, sans-serif",
  },
});

// 일정 계획 규칙 페이지
const LearningViewer = () => {
  const [enteredModelName, setEnteredModelName] = useState("");
  const [optionCheckboxes, setOptionCheckboxes] = useState(CHECKBOX_OPTIONS);
  const [schModelSelection, setSchModelSelection] = useState("Forward");
  const [enteredModelDetail, setEnteredModeDetail] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success', 'error', 'warning', 'info'
  });
  const { group_code, project_code } = useParams();
  const openSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };
  const closeSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };
  const { modelParamList } = useGetTableData2(
    true,
    `/api/rule/allRules?group_code=${group_code}&project_code=${project_code}`,
    "modelParamList"
  );
  const createModelParameter = useCreateModelData(true, "/api/rule/addRule");
  const updateModelParameterMutation = usePutData(
    true,
    "/api/rule/editRule",
    openSnackbar
  );
  const currentModelIdRef = useRef(null);
  const [editModelId, setEditModelId] = useState(null);
  const [sliderValues, setSliderValues] = useState({
    option1: 1,
    option2: 1,
    option3: 1,
    option4: 1,
  });
  const handleSelectionChange = (newSelection) => {
    setSchModelSelection(newSelection);
    if (newSelection === "Backward") {
      const newCheckboxes = { ...optionCheckboxes };
      CHECKBOX_OPTIONS.forEach((option) => {
        if (option.forwardOnly) {
          newCheckboxes[option.name] = false;
        }
      });
      setOptionCheckboxes(newCheckboxes);
    } else if (newSelection === "Back + Forward") {
      const newCheckboxes = { ...optionCheckboxes };
      CHECKBOX_OPTIONS.forEach((option) => {
        if (option.forwardOnly) {
          newCheckboxes[option.name] = false;
        }
      });
      setOptionCheckboxes(newCheckboxes);
    }
  };
  const handleCheckboxChange = useCallback(
    (e) => {
      const { name, checked } = e.target;
      const newCheckboxes = {
        ...optionCheckboxes,
        [name]: checked,
      };
      setOptionCheckboxes(newCheckboxes);
    },
    [optionCheckboxes]
  );
  const handleModelInputChange = useCallback((e) => {
    setEnteredModelName(e.target.value);
  }, []);
  const handleModelDetailInputChange = (e) => {
    setEnteredModeDetail(e.target.value);
  };
  const handleSubmitModel = async () => {
    if (enteredModelName.trim()) {
      if (enteredModelName.length > 15) {
        openSnackbar("규칙명은 15자 이내로 입력해 주세요.", "info");
        return;
      }
      if (enteredModelDetail.length > 15) {
        openSnackbar("규칙 설명은 15자 이내로 입력해 주세요.", "info");
        return;
      }
      if (isEditMode) {
        try {
          const obj = {
            model_id: editModelId,
            model_name: enteredModelName,
            model_description: enteredModelDetail,
            learning_direction:
              schModelSelection === "Forward"
                ? 1
                : schModelSelection === "Backward"
                ? 0
                : 2,
            leadtime_level: sliderValues.option1,
            schedule_adherence: sliderValues.option2,
            duedate_compliance: sliderValues.option3,
            order_priority: sliderValues.option4,
            group_code,
            project_code,
          };
          updateModelParameterMutation.mutate(obj, {
            onSuccess: () => {
              setSliderValues({
                option1: 1,
                option2: 1,
                option3: 1,
                option4: 1,
              });
              setIsEditMode(false);
              setEditModelId(null);
              setSchModelSelection("Forward");
              setEnteredModeDetail("");
              setEnteredModelName("");
            },
          });
        } catch (e) {
          console.log(e);
        }
      } else {
        try {
          const obj = {
            model_name: enteredModelName,
            model_description: enteredModelDetail,
            learning_direction:
              schModelSelection === "Forward"
                ? 1
                : schModelSelection === "Backward"
                ? 0
                : 2,
            leadtime_level: sliderValues.option1,
            schedule_adherence: sliderValues.option2,
            duedate_compliance: sliderValues.option3,
            order_priority: sliderValues.option4,
            group_code,
            project_code,
          };
          const modelId = await createModelParameter.mutateAsync(obj, {
            onSuccess: () => {
              setSliderValues({
                option1: 1,
                option2: 1,
                option3: 1,
                option4: 1,
              });
              setSchModelSelection("Forward");
              setEnteredModeDetail("");
              setEnteredModelName("");
              openSnackbar("규칙을 추가하였습니다.");
            },
          });
          currentModelIdRef.current = modelId;
          return modelId;
        } catch (e) {
          console.log(e);
        }
      }
    } else {
      openSnackbar("규칙 이름을 입력해 주세요.", "info");
      return;
    }
  };
  const closeEditModeButtonChip = () => {
    setSliderValues({ option1: 1, option2: 1, option3: 1, option4: 1 });
    openSnackbar("수정을 종료합니다.");
    setIsEditMode(false);
    setEnteredModeDetail("");
    setEnteredModelName("");
    setSchModelSelection("Forward");
  };
  return (
    <>
      <ThemeProvider theme={theme}>
        <Wrapper selection={schModelSelection}>
          <InnerWrapper>
            <InnerSection>
              <InnerSectionBox>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "1rem",
                    height: "40px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Title>규칙 {isEditMode ? "수정" : "생성"}&nbsp; </Title>
                    <Tooltip
                      title="이동"
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        goToHelpLink(
                          "https://help.ese-tech.com/ko/scheduler#h-6-%EC%9D%BC%EC%A0%95-%EA%B3%84%ED%9A%8D-%EA%B7%9C%EC%B9%99-%ED%8E%98%EC%9D%B4%EC%A7%80"
                        )
                      }
                    >
                      <HelpOutlineIcon />
                    </Tooltip>
                  </div>
                  {isEditMode && (
                    <Chip
                      label="수정중"
                      color="success"
                      icon={<ModeEditIcon />}
                      onDelete={closeEditModeButtonChip}
                      sx={{
                        p: 2,
                        fontSize: "0.88rem",
                        "& .MuiChip-icon": {
                          color: "white !important",
                          fontSize: "1.2rem",
                        },
                        "& .MuiChip-deleteIcon": {
                          color: "#fa4b4b !important",
                          marginLeft: "1px",
                          fontSize: "1.2rem",
                        },
                      }}
                    />
                  )}
                </div>
                <InnerSectionContentContainer>
                  <SectionBox>
                    <ModelInput
                      modelInput={enteredModelName}
                      onModelInputChange={handleModelInputChange}
                      handleModelDetailInputChange={
                        handleModelDetailInputChange
                      }
                      enteredModelDetail={enteredModelDetail}
                    />
                  </SectionBox>
                  <SectionBox>
                    <SchedulingSelector
                      selection={schModelSelection}
                      onSelectionChange={handleSelectionChange}
                    />
                  </SectionBox>
                  <SectionBox>
                    <OptionsArea
                      checkboxes={optionCheckboxes}
                      onCheckboxChange={handleCheckboxChange}
                      selection={schModelSelection}
                      sliderValues={sliderValues}
                      setSliderValues={setSliderValues}
                    />
                  </SectionBox>
                  <InnerFooterBtnBox>
                    <Button
                      color="primary"
                      variant="contained"
                      sx={{ width: "100%", fontSize: "1.2rem" }}
                      onClick={handleSubmitModel}
                      // disabled={modelProgressStatus === 9}
                    >
                      {isEditMode ? "수정 완료" : "저장"}
                    </Button>
                  </InnerFooterBtnBox>
                </InnerSectionContentContainer>
              </InnerSectionBox>
            </InnerSection>
            <InnerSection>
              <RuleList
                modelParamList={modelParamList}
                setSliderValues={setSliderValues}
                setEditModelId={setEditModelId}
                openSnackbar={openSnackbar}
                setIsEditMode={setIsEditMode}
                editModelId={editModelId}
                isEditMode={isEditMode}
                closeEditModeButtonChip={closeEditModeButtonChip}
                setSchModelSelection={setSchModelSelection}
                setEnteredModelName={setEnteredModelName}
                setEnteredModeDetail={setEnteredModeDetail}
                group_code={group_code}
                project_code={project_code}
              />
            </InnerSection>
          </InnerWrapper>
        </Wrapper>
      </ThemeProvider>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LearningViewer;
